import type { LatencyEventName, LatencyTurn, SpeechOutcome, SpeechTextSource, VoiceStart } from '../libs/velis-latency'

import { defineStore } from 'pinia'
import { ref } from 'vue'

import { addLatencyEvent, deriveLatencyTurn, emptyLatencyTurn } from '../libs/velis-latency'

const turnLimit = 8

export type VoiceWarmPhase = 'not-loaded' | 'warming' | 'ready' | 'failed'

export interface VoiceWarmStatus {
  phase: VoiceWarmPhase
  voiceId: string
  weightsReady: boolean
  /** Null until one synthesis reports whether it skipped load and weight switch. */
  warmPath: boolean | null
}

const emptyVoiceWarm: VoiceWarmStatus = {
  phase: 'not-loaded',
  voiceId: '',
  weightsReady: false,
  warmPath: null,
}

/**
 * Keeps the latest latency turns for the workbench.
 * The stage window records them. `state: true` copies the list to the settings window.
 * A remote snapshot replaces the list. This store has no watcher that writes it again.
 */
export const useVelisLatencyStore = defineStore('velis-latency', () => {
  const turns = ref<LatencyTurn[]>([])
  const voiceWarm = ref<VoiceWarmStatus>({ ...emptyVoiceWarm })

  function upsert(turnId: string, update: (turn: LatencyTurn) => LatencyTurn) {
    const current = turns.value.find(turn => turn.turnId === turnId) ?? emptyLatencyTurn(turnId)
    const next = update(current)
    const rest = turns.value.filter(turn => turn.turnId !== turnId)
    turns.value = [...rest, next].slice(-turnLimit)
  }

  function recordEvent(turnId: string, name: LatencyEventName, at = Date.now()) {
    if (!turnId)
      return
    upsert(turnId, turn => addLatencyEvent(turn, { name, at }))
  }

  function recordVoiceWarm(status: VoiceWarmStatus) {
    voiceWarm.value = status
  }

  function recordFacts(turnId: string, facts: {
    speechTextSource?: SpeechTextSource
    voiceStart?: VoiceStart
    speechOutcome?: SpeechOutcome
    warmPath?: boolean
    measured?: LatencyTurn['measured']
  }) {
    if (!turnId)
      return
    upsert(turnId, turn => deriveLatencyTurn({
      ...turn,
      ...(facts.speechTextSource ? { speechTextSource: facts.speechTextSource } : {}),
      ...(facts.voiceStart ? { voiceStart: facts.voiceStart } : {}),
      ...(facts.speechOutcome ? { speechOutcome: facts.speechOutcome } : {}),
      ...(facts.warmPath === undefined ? {} : { warmPath: facts.warmPath }),
      measured: {
        ...turn.measured,
        ...facts.measured,
      },
    }))
  }

  return { recordEvent, recordFacts, recordVoiceWarm, turns, voiceWarm }
}, {
  synced: {
    state: true,
  },
})
